import { formatDate, sortAsc } from '@strata/helper-functions';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { filterDataFromSearch, Search } from '@strata/react-filter-pane';
import { FormSection } from '@strata/react-form-section';
import { Spinner } from '@strata/react-loading-animation';
import dayjs from 'dayjs';
import { ComponentProps, useMemo, useState } from 'react';

import { RuleToggleButton } from 'components/admin/automations/RuleToggleButton';
import {
    AutomationRule,
    AutomationTemplateType,
    AutomationTemplateTypes,
} from 'services/AutomationRuleHelpers';

const getDateRangeString = (rule: AutomationRule) => {
    if (!(rule.startDate && rule.endDate)) {
        return undefined;
    }

    return `${formatDate(rule.startDate)} - ${formatDate(rule.endDate)}`;
};

const isDateValid = (rule: AutomationRule) => {
    let dateRangeValid = true;
    if (dayjs().isBefore(rule.startDate, 'day') || dayjs().isAfter(rule.endDate, 'day')) {
        dateRangeValid = false;
    }
    return dateRangeValid;
};

interface RulesSidebarSectionProps {
    group: string;
    groupRules: AutomationRule[];
    searchText: string;
    onRuleClick: (rule: AutomationRule) => void;
    onRuleToggle: (rule: AutomationRule) => void;
    userCanEdit: boolean;
}

const RulesSidebarSection = ({
    group,
    groupRules,
    searchText,
    userCanEdit,
    onRuleClick,
    onRuleToggle,
}: RulesSidebarSectionProps) => (
    <div className="padding__bottom--medium">
        <CollapsibleSection
            className="sds_automations__rule-group"
            title={`${group} Rules`}
            fill
            content={
                groupRules.length > 0 ? (
                    filterDataFromSearch(groupRules, searchText)
                        ?.sort((a, b) => sortAsc(a.description, b.description))
                        .map((rule) => (
                            <div
                                key={rule.id}
                                className="sds_automations__rule"
                                role="button"
                                tabIndex={0}
                                onClick={() => onRuleClick(rule)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        onRuleClick(rule);
                                    }
                                }}
                            >
                                <RuleToggleButton
                                    label={rule.description}
                                    helperText={getDateRangeString(rule)}
                                    isOn={rule.isActive}
                                    disabled={!userCanEdit}
                                    onChange={() => onRuleToggle(rule)}
                                    dateRangeValid={isDateValid(rule)}
                                />
                            </div>
                        ))
                ) : (
                    <div className="sds_automations__rule-group--no-rules">
                        {`No ${group.toLowerCase()} rules created.`}
                    </div>
                )
            }
        />
    </div>
);

interface RulesSidebarProps
    extends Pick<
        ComponentProps<typeof RulesSidebarSection>,
        'onRuleClick' | 'onRuleToggle' | 'userCanEdit'
    > {
    rules?: AutomationRule[];
}

export const RulesSidebar = ({
    rules = undefined,
    onRuleClick,
    onRuleToggle,
    userCanEdit,
}: RulesSidebarProps) => {
    const [searchText, setSearchText] = useState('');

    const groupedRules = useMemo(() => {
        const ruleGroups = Object.fromEntries(
            Object.values(AutomationTemplateTypes).map<[AutomationTemplateType, AutomationRule[]]>(
                (template) => [template, []],
            ),
        );
        rules?.forEach((rule) => {
            if (ruleGroups[rule.template]) {
                ruleGroups[rule.template].push(rule);
            }
        });
        return Object.entries(ruleGroups).sort(([a], [b]) => sortAsc(a, b));
    }, [rules]);

    return (
        <FormSection
            sectionTitle="Rules"
            className="sds_automations__rules-sidebar"
            content={
                !rules ? (
                    <Spinner intent={Spinner.Intent.PRIMARY} />
                ) : (
                    <>
                        <Search
                            className="margin__bottom--medium"
                            value={searchText}
                            onSearch={setSearchText}
                        />
                        {groupedRules.map(([group, groupRules]) => (
                            <RulesSidebarSection
                                key={group}
                                group={group}
                                groupRules={groupRules}
                                searchText={searchText}
                                userCanEdit={userCanEdit}
                                onRuleClick={onRuleClick}
                                onRuleToggle={onRuleToggle}
                            />
                        ))}
                    </>
                )
            }
        />
    );
};
